import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import {ArrowRight, Lock, Mail} from 'lucide-react';
import {GuestAccountState} from '@chekin/api';
import {PATTERNS} from '@chekin/core';
import {
  Button,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  OtpResend,
  PasswordStrength,
  showToast,
} from '@chekinapp/ui';
import {FormTypes} from '../../types';
import {CREATE_ACCOUNT_STEPS, FORM_NAMES} from '../../constants';
import {AuthIdentityChip} from '../AuthIdentityChip';
import {useSignIn} from 'hooks/auth/useSignIn';
import {useSignUp} from 'hooks/auth/useSignUp';
import {useCheckEmailMutation} from 'hooks/auth/useCheckEmailMutation';
import {useAuthErrorMessage} from 'hooks/auth/useAuthErrorMessage';
import {useAnalytics, POSTHOG_EVENTS, deriveAuthFailureReason} from 'analytics';

const STEP_BY_ACCOUNT_STATE: Record<GuestAccountState, CREATE_ACCOUNT_STEPS> = {
  has_password: CREATE_ACCOUNT_STEPS.login,
  no_account: CREATE_ACCOUNT_STEPS.password,
  social_only: CREATE_ACCOUNT_STEPS.ssoOnly,
};

type Props = {
  step: CREATE_ACCOUNT_STEPS;
  onConfirm: (nextStep: CREATE_ACCOUNT_STEPS) => void;
  onGoBack: () => void;
  onSuccessAuth: () => void;
  isBusy?: boolean;
};

export const AccountForm = ({
  step,
  onConfirm,
  onGoBack,
  onSuccessAuth,
  isBusy,
}: Props) => {
  const [resendCount, setResendCount] = useState(0);
  const {t} = useTranslation();
  const {track} = useAnalytics();
  const getErrorMessage = useAuthErrorMessage();

  const isEmailStep = step === CREATE_ACCOUNT_STEPS.email;
  const isLoginStep = step === CREATE_ACCOUNT_STEPS.login;
  const isPasswordStep = step === CREATE_ACCOUNT_STEPS.password;
  const isCheckEmailCodeStep = step === CREATE_ACCOUNT_STEPS.checkEmailCode;

  const {
    handleSubmit,
    register,
    control,
    watch,
    setError,
    clearErrors,
    formState: {errors},
    getValues,
  } = useFormContext<FormTypes>();

  const {mutate: checkEmail, isPending: isCheckingEmail} = useCheckEmailMutation({
    onSuccess: accountState => {
      track(POSTHOG_EVENTS.AUTH_EMAIL_CHECKED, {account_state: accountState});
      onConfirm(STEP_BY_ACCOUNT_STATE[accountState]);
    },
    onError: error => {
      track(POSTHOG_EVENTS.AUTH_FAILED, {
        method: 'email_check',
        reason: deriveAuthFailureReason(error),
      });
      showToast.error(getErrorMessage(error, 'auth.check_email_error'));
    },
  });

  const {signIn, isPending: isSignInLoading} = useSignIn({
    onSuccess: () => {
      track(POSTHOG_EVENTS.AUTH_SUCCEEDED, {method: 'email'});
      onSuccessAuth();
    },
    onInvalid: () => {
      track(POSTHOG_EVENTS.AUTH_FAILED, {method: 'email', reason: 'invalid_credentials'});
      setError(FORM_NAMES.password, {message: t('auth.error.wrong_password')});
    },
    onError: error =>
      track(POSTHOG_EVENTS.AUTH_FAILED, {
        method: 'email',
        reason: deriveAuthFailureReason(error),
      }),
  });

  const {
    signUp,
    confirmSignUp,
    isLoading: isSignUpLoading,
  } = useSignUp({
    onSignUpSuccess: () => {
      track(POSTHOG_EVENTS.SIGNUP_CODE_SENT);
      onConfirm(CREATE_ACCOUNT_STEPS.checkEmailCode);
    },
    onSignUpError: error =>
      track(POSTHOG_EVENTS.AUTH_FAILED, {
        method: 'email_signup',
        reason: deriveAuthFailureReason(error),
      }),
    onSignUpConfirmationSuccess: () => {
      track(POSTHOG_EVENTS.AUTH_SUCCEEDED, {method: 'email_signup'});
      onSuccessAuth();
    },
    onSignUpConfirmationError: error =>
      track(POSTHOG_EVENTS.SIGNUP_CODE_FAILED, {reason: deriveAuthFailureReason(error)}),
  });

  const isLoading =
    isCheckingEmail || isSignInLoading || isSignUpLoading || Boolean(isBusy);
  const email = watch(FORM_NAMES.email) ?? '';
  const password = watch(FORM_NAMES.password) ?? '';

  const onSubmit = useCallback(
    (data: FormTypes) => {
      if (isEmailStep) {
        checkEmail({email: data.email});
        return;
      }

      if (isLoginStep) {
        track(POSTHOG_EVENTS.AUTH_STARTED, {method: 'email'});
        signIn({email: data.email, password: data.password});
        return;
      }

      if (isPasswordStep) {
        track(POSTHOG_EVENTS.AUTH_STARTED, {method: 'email_signup'});
        signUp({email: data.email, password: data.password});
        return;
      }

      if (isCheckEmailCodeStep) {
        confirmSignUp({code: getValues(FORM_NAMES.email_code)});
      }
    },
    [
      isEmailStep,
      isLoginStep,
      isPasswordStep,
      isCheckEmailCodeStep,
      checkEmail,
      signIn,
      signUp,
      confirmSignUp,
      getValues,
      track,
    ],
  );

  const handleResendCode = useCallback(() => {
    setResendCount(count => count + 1);
    signUp({
      email: getValues(FORM_NAMES.email),
      password: getValues(FORM_NAMES.password),
    });
  }, [signUp, getValues]);

  const submitLabel = (() => {
    if (isLoginStep) return t('auth.log_in');
    if (isPasswordStep) return t('auth.create_account.create_cta');
    if (isCheckEmailCodeStep) return t('auth.create_account.verify_cta');
    return t('continue');
  })();

  return (
    <form
      className="flex flex-col gap-4 [--field-max-width:100%]"
      onSubmit={handleSubmit(onSubmit)}
    >
      {isEmailStep && (
        <Input
          type="email"
          label={t('email')}
          {...register(FORM_NAMES.email, {
            required: t('required'),
            pattern: {
              value: PATTERNS.email,
              message: t('invalid_email'),
            },
          })}
          leftIcon={<Mail size={18} />}
          error={errors.email?.message}
          disabled={isLoading}
          autoComplete="email"
          autoCorrect="off"
          spellCheck={false}
        />
      )}

      {(isLoginStep || isPasswordStep) && (
        <>
          <AuthIdentityChip email={email} onChange={onGoBack} disabled={isLoading} />
          <div className="flex flex-col gap-2">
            <Input
              type="password"
              label={t('password')}
              leftIcon={<Lock size={18} />}
              {...register(FORM_NAMES.password, {
                required: t('required'),
                onChange: () => clearErrors(FORM_NAMES.password),
              })}
              error={errors.password?.message}
              disabled={isLoading}
              autoFocus
              autoComplete={isLoginStep ? 'current-password' : 'new-password'}
            />
            {isPasswordStep && <PasswordStrength password={password} />}
          </div>
        </>
      )}

      {isCheckEmailCodeStep && (
        <Controller
          name={FORM_NAMES.email_code}
          control={control}
          rules={{required: t('required')}}
          defaultValue=""
          render={({field, fieldState: {error}}) => (
            <div className="flex flex-col gap-3">
              <InputOTP
                value={field.value || ''}
                onChange={field.onChange}
                maxLength={6}
                disabled={isLoading}
                className="mx-auto"
              >
                {Array.from({length: 6}).map((_, idx) => (
                  <InputOTPGroup key={idx}>
                    <InputOTPSlot index={idx} className="bg-white" />
                  </InputOTPGroup>
                ))}
              </InputOTP>
              {error?.message && (
                <p className="text-center text-[12px] font-medium text-[var(--chekin-text-danger)]">
                  {error.message}
                </p>
              )}
              <OtpResend
                onResend={handleResendCode}
                isResending={isSignUpLoading}
                resetKey={resendCount}
              />
            </div>
          )}
        />
      )}

      <Button type="submit" loading={isLoading} className="w-full">
        {submitLabel}
        <ArrowRight size={16} />
      </Button>

      {isCheckEmailCodeStep && (
        <Button
          variant="link"
          onClick={onGoBack}
          disabled={isLoading}
          className="mx-auto text-[13px] font-semibold"
        >
          ← {t('auth.create_account.use_different_email')}
        </Button>
      )}
    </form>
  );
};
